/*
 * Copyright (c) 2025. Sergey S. Chernov - All Rights Reserved
 *
 *  You may use, distribute and modify this code under the
 * terms of the private license, which you must obtain from the author
 *
 * To obtain the license, contact the author: https://t.me/real_sergeych or email to
 * real dot sergeych at gmail.
 */

package net.sergeych.crypto2

import com.ionspin.kotlin.crypto.generichash.GenericHash
import com.ionspin.kotlin.crypto.util.encodeToUByteArray
import kotlinx.coroutines.channels.ReceiveChannel
import kotlinx.coroutines.flow.Flow
import net.sergeych.bintools.ByteChunk
import net.sergeych.bintools.asChunk
import org.kotlincrypto.hash.sha3.SHA3_256
import org.kotlincrypto.hash.sha3.SHA3_384

interface StreamProcessor {
    fun update(data: UByteArray)
    fun final(): UByteArray
}

/**
 * Hash support for crypto2. We implement only secure as for the crypto2 publication time functions,
 * and do not include broken algorithms like SHA1 and SHA2, Md5, etc. To calculate hashes use:
 *
 * - [digest] to calculate a hash of the block in memory
 * - [ofChannel] to calculate hash of long or slow data in a channel
 * - [ofFlow] to calculate hash of the data in the [Flow<UByteArray>]
 */
@Suppress("unused")
enum class Hash(
    private val direct: ((UByteArray) -> UByteArray)? = null,
    val streamProcessor: () -> StreamProcessor,
) {

    Blake2b(
        // direct blacke2 is faster than stream:
        { GenericHash.genericHash(it) }, {
            object : StreamProcessor {
                val state = GenericHash.genericHashInit()
                override fun update(data: UByteArray) {
                    GenericHash.genericHashUpdate(state, data)
                }

                override fun final(): UByteArray =
                    GenericHash.genericHashFinal(state)
            }
        }),
    Sha3_384(
        // direct Keccaak currently is slower
        null,
        {
            object : StreamProcessor {
                val state = SHA3_384()
                override fun update(data: UByteArray) {
                    state.update(data.asByteArray())
                }

                override fun final(): UByteArray = state.digest().asUByteArray()
            }
        }),
    Sha3_256(null,
        {
            object : StreamProcessor {
                val state = SHA3_256()
                override fun update(data: UByteArray) {
                    state.update(data.asByteArray())
                }

                override fun final(): UByteArray = state.digest().asUByteArray()
            }
        }),

    /**
     * Synthetic hash contains of [Blake2b] and [Sha3_384] hashes concatenated in this order. Let calculate
     * compound hash using [ofFlow] or [ofChannel] effectively.
     */
    Sha3AndBlake(null,
        {
            object : StreamProcessor {
                val blake = Blake2b.streamProcessor()
                val sha384 = Sha3_384.streamProcessor()
                override fun update(data: UByteArray) {
                    blake.update(data)
                    sha384.update(data)
                }

                override fun final(): UByteArray = blake.final() + sha384.final()
            }
        });

    @Deprecated("will be removed in favor of digest()", ReplaceWith("digest()"))
    fun perform(src: UByteArray): UByteArray = digest(src)

    /**
     * Calculate digest for the in-memory data
     * @param src data to calculate hash digest over
     * @return calculated hash value
     */
    fun digest(src: UByteArray): UByteArray = direct?.invoke(src) ?: streamProcessor().also { it.update(src) }.final()

    /**
     * Collect the flow and return the hash digest of all the data. Let calculate hashes on data
     * that are too big to fit in memory
     */
    suspend fun ofFlow(source: Flow<UByteArray>): UByteArray {
        val sp = streamProcessor()
        source.collect { sp.update(it) }
        return sp.final()
    }

    /**
     * Read all data from the channel and return the hash digest of all the data. Let calculate hashes on data
     * that are too big to fit in memory
     */
    suspend fun ofChannel(source: ReceiveChannel<UByteArray>): UByteArray {
        val sp = streamProcessor()
        for (block in source) sp.update(block)
        return sp.final()
    }

    /**
     * Derive a salt of any size from a text. ___Salt could not be used as a password key___ source as it is not
     * strong to brute force, but it is good when you just need to get a deterministic salt of arbitrary size.
     *
     * _Note that deriving salt of sizes less than hash block will reduce hash strength and should not be allowed
     * in a situation where strength is of concern while extending its length above hash block size does not improve
     * it_. The only reason to use this function is when the desired _salt size_ is not equal to block size, or
     * not known beforehand.
     *
     * To get a cryptographically safe (more or less) key from password use [KDF] classes, or [KDF.deriveKey]
     * and [KDF.deriveMultipleKeys].
     */
    fun deriveSalt(base: String, sizeInBytes: Int): UByteArray =
        deriveSaltFromBytes(base.encodeToUByteArray(), sizeInBytes)

    /**
     * Derive a salt of any size from binary data.
     *
     * @see deriveSalt
     */
    fun deriveSaltFromBytes(base: UByteArray, sizeInBytes: Int): UByteArray {
        require(sizeInBytes > 0)
        val result = mutableListOf<UByte>()
        var round = 0
        var src = base
        do {
            src = "rnd_${round++}_".encodeToUByteArray() + src
            val hash = digest(src)
            if (result.size + hash.size <= sizeInBytes) result += hash
            else result += hash.copyOf(sizeInBytes - result.size)
        } while (result.size < sizeInBytes)
        return result.toUByteArray()
    }

    /**
     * Typo-compatible alias for [deriveSaltFromBytes].
     */
    fun deriveSaltFormBytes(base: UByteArray, sizeInBytes: Int): UByteArray =
        deriveSaltFromBytes(base, sizeInBytes)


}

private val defaultSuffix1 = "All lay loads on a willing horse".encodeToUByteArray()
private val defaultSuffix2 = "A stitch in time saves nine".encodeToUByteArray()

/**
 * Caclulate [Hash.Blake2b] hash for [src], shortcut for [Hash.Blake2b.digest]
 */
fun blake2b(src: UByteArray): UByteArray = Hash.Blake2b.digest(src)

fun blake2b(src: ByteChunk): ByteChunk = blake2b(src.data).asChunk()

/**
 * Double linked Blake2b using the default or specified suffix. This should be more hard to
 * brute force.collision attack than just [blake2b]. Note that different suffixes provide different
 * results.
 */
fun blake2b2l(src: UByteArray, suffix: UByteArray = defaultSuffix1): UByteArray =
    blake2b(blake2b(src) + suffix + src)

/**
 * Triple linked [blake2b], even more prone to collision attacks than [blake2b2l].
 */
fun blake2b3l(src: UByteArray): UByteArray = blake2b(blake2b2l(src) + defaultSuffix2 + src)
